// Punto de venta como el «Confección y Pago Tipo Punto de Venta» de Random (pedido de Rodrigo,
// 2026-10-22): los medios de pago van EN la pantalla de la venta, no en un diálogo aparte. A la
// izquierda los medios (efectivo, débito, crédito, transferencia, cheque; varios a la vez); a la
// derecha el RESUMEN CUENTA (venta, pagos, saldo) y, en efectivo, el vuelto con su composición en
// billetes y monedas. «Grabar e imprimir» se habilita cuando los pagos cubren la venta exacta.
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { MedioCaja } from '../api/contratos';
import { SelectorBanco } from '../comun/bancos';
import { pesos } from '../comun/formato';
import { entero, filaNueva, NOMBRE_MEDIO, problemaDe, type Fila } from '../comun/medios';

/** Lo que entra físicamente al mesón exige el turno de caja abierto (la transferencia no). */
const FISICOS: readonly MedioCaja[] = ['EFECTIVO', 'DEBITO', 'CREDITO', 'CHEQUE'];

/** Billetes y monedas chilenos, del mayor al menor (composición del vuelto, como Random). */
const DENOMINACIONES = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10, 1] as const;
const NOMBRE_DENOMINACION: Record<number, string> = {
  20000: 'Veinte mil',
  10000: 'Diez mil',
  5000: 'Cinco mil',
  2000: 'Dos mil',
  1000: 'Mil',
  500: 'Quinientos',
  100: 'Cien',
  50: 'Cincuenta',
  10: 'Diez',
  1: 'Pesos',
};

export function composicionVuelto(vuelto: number): Array<{ valor: number; cantidad: number }> {
  let resto = Math.max(Math.round(vuelto), 0);
  return DENOMINACIONES.map((valor) => {
    const cantidad = valor === 1 ? resto : Math.floor(resto / valor);
    resto -= cantidad * valor;
    return { valor, cantidad };
  }).filter((x) => x.cantidad > 0);
}

/** Lo que los pagos dicen de la venta: cuánto se paga, saldo, vuelto y si está lista para grabar. */
export function estadoPago(total: number, filas: readonly Fila[], sinTurno: boolean) {
  const pagos = filas.reduce((s, f) => s + (entero(f.monto) || 0), 0);
  const vuelto = filas.reduce(
    (s, f) =>
      s +
      (f.medio === 'EFECTIVO' && f.entrega ? Math.max(entero(f.entrega) - entero(f.monto), 0) : 0),
    0,
  );
  const problemas = filas.map((f) =>
    sinTurno && FISICOS.includes(f.medio)
      ? 'abre tu turno de caja (o cobra por transferencia)'
      : problemaDe(f),
  );
  const saldo = total - pagos;
  return {
    pagos,
    saldo,
    vuelto: Number.isFinite(vuelto) ? vuelto : 0,
    problemas,
    listo: total > 0 && saldo === 0 && problemas.every((p) => !p),
  };
}

const MEDIOS = Object.keys(NOMBRE_MEDIO) as MedioCaja[];

/**
 * El medio de pago como lista desplegable manejada con el teclado (pedido de Rodrigo, 2026-10-23):
 * Enter (o ↓) la abre; ↑↓ recorren; Enter elige y pasa al monto; Esc cierra; 1–5 eligen directo.
 * Con el mouse: clic abre y clic elige.
 */
function SelectorMedio({
  etiqueta,
  valor,
  deshabilitado,
  alElegir,
  alOtroMedio,
}: {
  etiqueta: string;
  valor: MedioCaja;
  deshabilitado: boolean;
  alElegir: (m: MedioCaja) => void;
  alOtroMedio: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [marcado, setMarcado] = useState(MEDIOS.indexOf(valor));
  const abrir = () => {
    setMarcado(MEDIOS.indexOf(valor));
    setAbierto(true);
  };
  const elegir = (k: number) => {
    setAbierto(false);
    alElegir(MEDIOS[k]!);
  };
  const tecla = (ev: KeyboardEvent<HTMLButtonElement>) => {
    if (ev.altKey && (ev.key === 'o' || ev.key === 'O')) {
      ev.preventDefault();
      alOtroMedio();
      return;
    }
    if (/^[1-9]$/.test(ev.key) && Number(ev.key) <= MEDIOS.length) {
      ev.preventDefault();
      elegir(Number(ev.key) - 1);
      return;
    }
    if (!abierto) {
      if (ev.key === 'Enter' || ev.key === 'ArrowDown' || ev.key === ' ') {
        ev.preventDefault();
        abrir();
      }
      return;
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      const d = ev.key === 'ArrowDown' ? 1 : -1;
      setMarcado((x) => (x + d + MEDIOS.length) % MEDIOS.length);
    } else if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      elegir(marcado);
    } else if (ev.key === 'Escape' || ev.key === 'Tab') {
      if (ev.key === 'Escape') ev.preventDefault();
      setAbierto(false);
    }
  };
  return (
    <div className="selector-medio">
      <button
        type="button"
        aria-label={etiqueta}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        className="selector-medio-boton"
        disabled={deshabilitado}
        onClick={() => (abierto ? setAbierto(false) : abrir())}
        onKeyDown={tecla}
        onBlur={() => setAbierto(false)}
      >
        <span>{NOMBRE_MEDIO[valor]}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {abierto && (
        <ul className="selector-medio-lista" role="listbox" aria-label={`${etiqueta}: elegir`}>
          {MEDIOS.map((m, k) => (
            <li
              key={m}
              role="option"
              aria-selected={k === marcado}
              className={k === marcado ? 'marcado' : ''}
              // mousedown: elige antes de que el botón pierda el foco y cierre la lista.
              onMouseDown={(ev) => {
                ev.preventDefault();
                elegir(k);
              }}
              onMouseEnter={() => setMarcado(k)}
            >
              <kbd>{k + 1}</kbd> {NOMBRE_MEDIO[m]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PagoMeson({
  total,
  etiquetaTotal = 'Venta',
  desglose = [],
  aviso,
  filas,
  sinTurno,
  deshabilitado,
  alCambiar,
  alIrAGrabar,
  alAbrirCaja,
}: {
  /** Total bruto de la venta (lo calculado por el backend); 0 mientras no hay cálculo. */
  total: number;
  /** La nota por emitir se cobra por su saldo: «Por pagar» en vez de «Venta». */
  etiquetaTotal?: string;
  /**
   * Punto de venta sin el pie negro (Rodrigo, 2026-10-24: «es duplicidad»): el desglose (neto,
   * impuestos, cargos) y los avisos del total van aquí, en el resumen de cuenta.
   */
  desglose?: Array<{ etiqueta: string; monto: string }>;
  aviso?: ReactNode;
  filas: Fila[];
  sinTurno: boolean;
  deshabilitado: boolean;
  alCambiar: (filas: Fila[]) => void;
  /** Los pagos cuadran y se dio Enter en el último dato: el foco pasa a «Grabar e imprimir». */
  alIrAGrabar: () => void;
  /** Sin turno: el aviso es un botón que abre la caja ahí mismo (Rodrigo, 2026-10-24). */
  alAbrirCaja?: () => void;
}) {
  const e = estadoPago(total, filas, sinTurno);
  const poner = (i: number, c: Partial<Fila>) =>
    alCambiar(filas.map((f, j) => (j === i ? { ...f, ...c } : f)));
  const otroMedio = () =>
    alCambiar([...filas, filaNueva(e.saldo > 0 ? String(e.saldo) : '', 'DEBITO')]);
  // Todo con el teclado (pedido de Rodrigo, 2026-10-23): Medio (flechas, Enter) → Monto (Enter
  // vacío pone lo que falta; Enter confirma) → detalle de cómo paga (Enter de dato en dato) → si los
  // pagos cuadran, «Grabar e imprimir» (Enter); si falta, otra fila de medio. Alt+O agrega un medio.
  const raiz = useRef<HTMLElement>(null);
  const enfocar = (selector: string) =>
    setTimeout(() => raiz.current?.querySelector<HTMLElement>(selector)?.focus(), 0);
  const avanzar = () => {
    if (e.listo) alIrAGrabar();
    else if (e.saldo > 0 && filas.length < 6) {
      alCambiar([...filas, filaNueva('', 'DEBITO')]);
      enfocar(`[aria-label="Medio ${filas.length + 1}"]`);
    }
  };
  const tecla = (ev: KeyboardEvent<HTMLElement>) => {
    if (ev.altKey && (ev.key === 'o' || ev.key === 'O')) {
      ev.preventDefault();
      otroMedio();
      return;
    }
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    const el = ev.currentTarget;
    const fila = el.closest('tr');
    const i = Number(fila?.dataset['fila'] ?? 0);
    const etiqueta = el.getAttribute('aria-label') ?? '';
    if (etiqueta.startsWith('Medio ')) {
      enfocar(`[aria-label="Monto ${i + 1}"]`);
      return;
    }
    if (etiqueta.startsWith('Monto ')) {
      const propio = entero(filas[i]!.monto) || 0;
      if (!(propio > 0)) {
        // Primer Enter con el monto vacío: lo que falta para cuadrar la venta.
        const falta = total - (e.pagos - propio);
        if (falta > 0) poner(i, { monto: String(falta) });
        return;
      }
      const detalle = fila?.querySelector<HTMLElement>('.cobro-detalle input');
      if (detalle) detalle.focus();
      else avanzar();
      return;
    }
    const datos = [...(fila?.querySelectorAll<HTMLElement>('.cobro-detalle input') ?? [])];
    const k = datos.indexOf(el);
    if (k >= 0 && k < datos.length - 1) datos[k + 1]!.focus();
    else avanzar();
  };
  const composicion = composicionVuelto(e.vuelto);

  return (
    <section className="pago-meson" aria-label="Pago de la venta" ref={raiz}>
      <div className="pago-meson-medios">
        <div className="pago-meson-titulo">
          <span>Medios de pago</span>
          {sinTurno &&
            (alAbrirCaja ? (
              <button
                type="button"
                className="insignia tono-aviso"
                data-sin-turno
                onClick={alAbrirCaja}
                title="Abre tu turno de caja para cobrar en efectivo, tarjeta o cheque"
              >
                caja cerrada · abrir caja
              </button>
            ) : (
              <span className="insignia tono-aviso" data-sin-turno>
                sin turno de caja: solo transferencia
              </span>
            ))}
        </div>
        <table className="cobro-medios pago-meson-tabla" aria-label="Medios de pago">
          <colgroup>
            <col className="c-medio" />
            <col className="c-monto" />
            <col />
            <col className="c-quitar" />
          </colgroup>
          <thead>
            <tr>
              <th>Medio</th>
              <th className="al-der">Monto</th>
              <th>Detalle</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => {
              const vuelto =
                f.medio === 'EFECTIVO' && f.entrega ? entero(f.entrega) - entero(f.monto) : NaN;
              return (
                <tr key={i} data-medio={f.medio} data-fila={i}>
                  <td>
                    <SelectorMedio
                      etiqueta={`Medio ${i + 1}`}
                      valor={f.medio}
                      deshabilitado={deshabilitado}
                      alElegir={(medio) => {
                        poner(i, { medio });
                        enfocar(`[aria-label="Monto ${i + 1}"]`);
                      }}
                      alOtroMedio={otroMedio}
                    />
                  </td>
                  <td>
                    <input
                      aria-label={`Monto ${i + 1}`}
                      className="celda-num fuerte"
                      inputMode="numeric"
                      value={f.monto}
                      placeholder={
                        e.saldo > 0 && !f.monto ? `Enter = ${pesos(String(e.saldo))}` : undefined
                      }
                      disabled={deshabilitado}
                      onFocus={(x) => x.target.select()}
                      onChange={(x) => poner(i, { monto: x.target.value })}
                      onKeyDown={tecla}
                    />
                  </td>
                  <td>
                    <div className="cobro-detalle">
                      {f.medio === 'EFECTIVO' && (
                        <>
                          <input
                            aria-label={`Entrega ${i + 1}`}
                            className="celda-num"
                            inputMode="numeric"
                            placeholder="paga con (opcional)"
                            value={f.entrega}
                            disabled={deshabilitado}
                            onChange={(x) => poner(i, { entrega: x.target.value })}
                            onKeyDown={tecla}
                          />
                          {vuelto > 0 && (
                            <span className="cobro-vuelto">
                              vuelto <strong className="mono">{pesos(String(vuelto))}</strong>
                            </span>
                          )}
                        </>
                      )}
                      {(f.medio === 'DEBITO' || f.medio === 'CREDITO') && (
                        <>
                          <input
                            aria-label={`Autorización ${i + 1}`}
                            placeholder="código de autorización (voucher)"
                            value={f.autorizacion}
                            disabled={deshabilitado}
                            onChange={(x) => poner(i, { autorizacion: x.target.value })}
                            onKeyDown={tecla}
                          />
                          {f.medio === 'CREDITO' && (
                            <input
                              aria-label={`Cuotas ${i + 1}`}
                              className="celda-num cobro-cuotas"
                              inputMode="numeric"
                              placeholder="cuotas"
                              value={f.cuotas}
                              disabled={deshabilitado}
                              onChange={(x) => poner(i, { cuotas: x.target.value })}
                              onKeyDown={tecla}
                            />
                          )}
                        </>
                      )}
                      {f.medio === 'TRANSFERENCIA' && (
                        <input
                          aria-label={`Operación ${i + 1}`}
                          placeholder="N° de operación"
                          value={f.referencia}
                          disabled={deshabilitado}
                          onChange={(x) => poner(i, { referencia: x.target.value })}
                          onKeyDown={tecla}
                        />
                      )}
                      {f.medio === 'CHEQUE' && (
                        <>
                          {/* Los bancos del maestro (2026-10-23): siempre igual escritos. */}
                          <SelectorBanco
                            etiqueta={`Banco ${i + 1}`}
                            vacio="Banco…"
                            valor={f.banco}
                            deshabilitado={deshabilitado}
                            alCambiar={(banco) => poner(i, { banco })}
                            alTecla={tecla}
                          />
                          <input
                            aria-label={`N° cheque ${i + 1}`}
                            placeholder="N° cheque"
                            value={f.numero}
                            disabled={deshabilitado}
                            onChange={(x) => poner(i, { numero: x.target.value })}
                            onKeyDown={tecla}
                          />
                          <input
                            aria-label={`Fecha cheque ${i + 1}`}
                            type="date"
                            value={f.fecha}
                            disabled={deshabilitado}
                            onChange={(x) => poner(i, { fecha: x.target.value })}
                            onKeyDown={tecla}
                          />
                        </>
                      )}
                      {e.problemas[i] && f.monto && (
                        <span className="apagado cobro-falta">falta: {e.problemas[i]}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {filas.length > 1 && (
                      <button
                        type="button"
                        className="boton-icono"
                        aria-label={`Quitar medio ${i + 1}`}
                        disabled={deshabilitado}
                        onClick={() => alCambiar(filas.filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="pago-meson-pie">
          <button
            type="button"
            onClick={otroMedio}
            disabled={deshabilitado || filas.length >= 6}
            className="pago-meson-otro"
          >
            + Otro medio <kbd>Alt+O</kbd>
          </button>
          <span className="apagado">
            {filas.length} medio{filas.length === 1 ? '' : 's'} · pagos {pesos(String(e.pagos))}
          </span>
        </div>
      </div>

      <dl className="pago-meson-resumen" aria-label="Resumen cuenta">
        <div className="titulo">Resumen cuenta</div>
        {desglose.map((d) => (
          <div key={d.etiqueta} className="desglose">
            <dt>{d.etiqueta}</dt>
            <dd className="mono">{pesos(d.monto)}</dd>
          </div>
        ))}
        <div className="venta">
          <dt>{etiquetaTotal}</dt>
          <dd className="mono" data-total>
            {pesos(String(total))}
          </dd>
        </div>
        <div>
          <dt>Pagos</dt>
          <dd className="mono">{pesos(String(e.pagos))}</dd>
        </div>
        <div className={`saldo ${e.saldo !== 0 ? 'pendiente' : 'cuadra'}`}>
          <dt>{e.saldo < 0 ? 'Sobra' : 'Saldo'}</dt>
          <dd className="mono" data-saldo-meson={e.saldo}>
            {pesos(String(Math.abs(e.saldo)))}
          </dd>
        </div>
        {e.vuelto > 0 && (
          <div className="vuelto">
            <dt>Vuelto</dt>
            <dd className="mono" data-vuelto-meson={e.vuelto}>
              {pesos(String(e.vuelto))}
            </dd>
          </div>
        )}
        {aviso && <div className="aviso-resumen">{aviso}</div>}
        {composicion.length > 0 && (
          <div className="composicion" aria-label="Composición del vuelto">
            {composicion.map((c) => (
              <span key={c.valor} title={NOMBRE_DENOMINACION[c.valor]}>
                {c.cantidad} × {pesos(String(c.valor))}
              </span>
            ))}
          </div>
        )}
      </dl>
    </section>
  );
}
